"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "@/components/Link";
import {
  Title,
  Text,
  Button,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import Breadcrumb from "@/components/Breadcrumb";
import FollowButton from "@/components/FollowButton";
import PopularModels from "@/components/PopularModels";
import CardGrid from "@/components/CardGrid";
import { NoResultsRescue } from "@/components/SearchRescue";
import FilterMenu from "@/components/FilterMenu";
import { useSearchContext } from "@/context/searchContext";
import { useGlobalData } from "@/context/globalContext";
import { generateBrandSlug } from "@/lib/shared/utils";
import { useItemsStore } from "@/stores/itemsStore";
import { useFiltersStore } from "@/stores/filtersStore";
import { useUiStore } from "@/stores/uiStore";
import MobileMenu from "@/components/layout/MobileMenu";
import SignInPrompt from "@/components/SignInPrompt";
import { IconChevronLeft } from "@tabler/icons-react";

import styles from "./styles.module.scss";

const MAX_DESCRIPTION_LENGTH = 100;

interface Props {
  brand: BrandFilter;
  category: string;
}

export default function BrandSearchCategoryPageContent({ brand, category }: Props) {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const filterPaneOpen = useUiStore((state) => state.filterPaneOpen);
  const { setFilterPaneOpen } = useUiStore((state) => state.actions);
  const { setPreloadBrand, loadOnScroll, updateKeywordSearch, items, loading, noResults } =
    useSearchContext();

  const { brands: loadedBrands } = useGlobalData();
  const itemModalOpen = useUiStore((state) => state.itemModalOpen);
  const favoritesModalOpen = useUiStore((state) => state.favoritesModalOpen);
  const priceMonitorOpen = useUiStore((state) => state.priceMonitorOpen);
  const showSignInPrompt = useItemsStore((state) => state.showSignInPrompt);
  const setShowSignInPrompt = useItemsStore((state) => state.actions.setShowSignInPrompt);
  const resetActiveFilters = useFiltersStore((state) => state.actions.resetActiveFilters);

  useEffect(() => {
    const loadPageData = async () => {
      const decodedCategory = decodeURIComponent(category);

      const foundBrand = loadedBrands?.find((b) => b.label === brand.label);
      if (foundBrand) {
        setPreloadBrand(foundBrand.label);
      }
      updateKeywordSearch(decodedCategory);
      setActiveCategory(decodedCategory);
    };

    loadPageData();
  }, [category, brand, loadedBrands, setPreloadBrand, updateKeywordSearch]);

  const handleResetFilters = () => {
    resetActiveFilters();
    router.push("/search");
  };

  const brandInitial = brand.label.charAt(0).toUpperCase();
  const shouldShowReadMore = brand.description && brand.description.length > MAX_DESCRIPTION_LENGTH;
  const displayDescription =
    shouldShowReadMore && !descriptionExpanded
      ? brand?.description?.slice(0, MAX_DESCRIPTION_LENGTH) + "..."
      : brand.description;

  return (
    <>
      <MobileMenu />
      <MantineBox
        className={styles.brandCategoryPage}
        data-slidein-open={itemModalOpen || favoritesModalOpen || priceMonitorOpen}
      >
        <aside className={styles.sidebar}>
          {filterPaneOpen ? (
            <MantineBox className={styles.filtersWrapper}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterPaneOpen(false)}
                leftSection={<IconChevronLeft size={16} />}
                className={styles.backButton}
                visibleFrom="lg"
              >
                Back to Brand
              </Button>
              <FilterMenu onReset={handleResetFilters} />
            </MantineBox>
          ) : (
            <MantineBox className={styles.sidebarContent}>
              <MantineBox className={styles.brandIdentity}>
                <Breadcrumb
                  items={[
                    { text: "Brands", link: "/brands" },
                    {
                      text: brand.label,
                      link: `/brands/${encodeURIComponent(generateBrandSlug(brand.label))}`,
                    },
                    { text: activeCategory, link: "" },
                  ]}
                />
                <MantineBox className={styles.brandLogo}>
                  {brand.image ? (
                    <MantineImage src={brand.image} alt={brand.label} width={80} height={80} />
                  ) : (
                    <MantineText span className={styles.brandInitial}>
                      {brandInitial}
                    </MantineText>
                  )}
                </MantineBox>
                <Title order={1} className={styles.brandName}>
                  {brand.label}
                </Title>
                <FollowButton type="brand" id={brand.id} size="large" brand={brand} />

                {brand.description && (
                  <MantineBox className={styles.descriptionSection}>
                    <Text size="sm" className={styles.description}>
                      {displayDescription}
                    </Text>
                    {shouldShowReadMore && (
                      <MantineUnstyledButton
                        className={styles.readMoreButton}
                        onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                      >
                        {descriptionExpanded ? "[− Show less]" : "[+ Read more]"}
                      </MantineUnstyledButton>
                    )}
                  </MantineBox>
                )}
              </MantineBox>
              <MantineBox className={styles.popularModelsSection}>
                <PopularModels brand={brand} activeCategory={activeCategory} />
                <Link href="/search" className={styles.advancedSearchLink}>
                  ADVANCED SEARCH
                </Link>
              </MantineBox>
            </MantineBox>
          )}
        </aside>

        <main className={styles.mainContent} onScroll={loadOnScroll}>
          <MantineBox className={styles.mobileBrandIdentity}>
            <Breadcrumb
              items={[
                { text: "Brands", link: "/brands" },
                {
                  text: brand.label,
                  link: `/brands/${encodeURIComponent(generateBrandSlug(brand.label))}`,
                },
                { text: activeCategory, link: "" },
              ]}
            />
            <MantineBox className={styles.mobileBrandHeader}>
              <MantineBox className={styles.brandLogo}>
                {brand.image ? (
                  <MantineImage src={brand.image} alt={brand.label} width={80} height={80} />
                ) : (
                  <MantineText span className={styles.brandInitial}>
                    {brandInitial}
                  </MantineText>
                )}
              </MantineBox>
              <MantineBox className={styles.mobileBrandInfo}>
                <Title order={1} className={styles.brandName}>
                  {brand.label}
                </Title>
                <FollowButton type="brand" id={brand.id} size="large" brand={brand} />
              </MantineBox>
            </MantineBox>

            {brand.description && (
              <MantineBox className={styles.descriptionSection}>
                <Text size="sm" className={styles.description}>
                  {displayDescription}
                </Text>
                {shouldShowReadMore && (
                  <MantineUnstyledButton
                    className={styles.readMoreButton}
                    onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  >
                    {descriptionExpanded ? "[− Show less]" : "[+ Read more]"}
                  </MantineUnstyledButton>
                )}
              </MantineBox>
            )}
          </MantineBox>

          <MantineBox className={styles.mobilePopularModels}>
            <PopularModels brand={brand} activeCategory={activeCategory} />
            <Link href="/search" className={styles.advancedSearchLink}>
              ADVANCED SEARCH
            </Link>
          </MantineBox>

          <CardGrid
            items={items}
            loading={loading}
            noResults={noResults}
            showProAd={false}
            enableImageSearch
          />
          {noResults && !loading && <NoResultsRescue />}
        </main>
      </MantineBox>
      {showSignInPrompt && <SignInPrompt onClose={() => setShowSignInPrompt(false)} />}
    </>
  );
}
